// src/components/MessageRenderer.js
import React, { useState } from 'react';
import { Copy, Check, Code, Eye, Paperclip, Image, Search } from 'lucide-react';
import './MessageRenderer.css';

const MessageRenderer = ({ content, isError, role, isSystemMessage, attachments = [], image = null }) => {
  // Detect if the text is Arabic or RTL language
  const isRTL = (text) => {
    const rtlRegex = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/;
    return rtlRegex.test(text);
  };

  // Determine direction based on content
  const dir = isRTL(content) ? 'rtl' : 'ltr';
  const [copiedStates, setCopiedStates] = useState({});
  const [previewStates, setPreviewStates] = useState({});

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedStates(prev => ({ ...prev, [id]: true }));
      setTimeout(() => {
        setCopiedStates(prev => ({ ...prev, [id]: false }));
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const togglePreview = (id) => {
    setPreviewStates(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const detectCodeBlocks = (text) => {
    const codeBlockRegex = /```([\w]*)?\n?([\s\S]*?)```/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    let blockId = 0;

    while ((match = codeBlockRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: text.slice(lastIndex, match.index)
        });
      }

      parts.push({
        type: 'code',
        language: match[1] || 'text',
        content: match[2].trim(),
        id: `code-${blockId++}`
      });

      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
      parts.push({
        type: 'text',
        content: text.slice(lastIndex)
      });
    }

    return parts.length > 0 ? parts : [{ type: 'text', content: text }];
  };

  const isWebContent = (code, language) => {
    const webLanguages = ['html', 'css', 'javascript', 'js'];
    const hasHtmlTags = code.includes('<html') || code.includes('<!DOCTYPE') || 
                       (code.includes('<head>') && code.includes('<body>')) ||
                       (code.includes('<style>') && code.includes('<script>'));
    return webLanguages.includes(language.toLowerCase()) || hasHtmlTags;
  };

  const renderWebPreview = (code) => {
    return (
      <iframe
        srcDoc={code}
        style={{
          width: '100%',
          height: '400px',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: '8px',
          backgroundColor: 'white'
        }}
        sandbox="allow-scripts"
      />
    );
  };

  const formatText = (text) => {
    return text.split('\n').map((line, index) => {
      // Check if line contains URLs or domain names
      const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+|[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(?:\/[^\s]*)?)/g;
      
      if (urlRegex.test(line)) {
        const parts = line.split(urlRegex);
        return (
          <span key={index}>
            {parts.map((part, partIndex) => {
              if (urlRegex.test(part)) {
                let href = part;
                if (!href.startsWith('http://') && !href.startsWith('https://')) {
                  href = 'https://' + href;
                }
                return (
                  <a
                    key={partIndex}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: '#4CAF50',
                      textDecoration: 'underline',
                      cursor: 'pointer'
                    }}
                  >
                    {part}
                  </a>
                );
              }
              return part;
            })}
            {index < text.split('\n').length - 1 && <br />}
          </span>
        );
      }
      
      return (
        <span key={index}>
          {line}
          {index < text.split('\n').length - 1 && <br />}
        </span>
      );
    });
  };

  const parts = detectCodeBlocks(content);

  return (
    <div className={`message-bubble ${isError ? 'error' : ''} ${isSystemMessage ? 'system' : ''}`} dir={dir}>
      {attachments.length > 0 && (
        <div className="message-attachments">
          {attachments.map((attachment, idx) => (
            <div key={idx} className="attachment-preview">
              {attachment.type === 'image' ? (
                <img 
                  src={attachment.data} 
                  alt={attachment.name}
                  className="attachment-image"
                  style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '8px' }}
                />
              ) : (
                <div className="attachment-file-container" style={{ marginBottom: '8px' }}>
                  <div className="attachment-file" style={{ 
                    padding: '8px 12px', 
                    background: 'rgba(255, 255, 255, 0.1)', 
                    borderRadius: '8px', 
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '4px'
                  }}>
                    <Paperclip size={16} />
                    <span>{attachment.name}</span>
                    <span style={{ fontSize: '11px', opacity: 0.7 }}>({(attachment.size / 1024).toFixed(1)} KB)</span>
                    {attachment.isPdfFile && attachment.previewUrl && (
                      <a 
                        href={attachment.previewUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        download={attachment.name}
                        style={{ 
                          marginLeft: '8px', 
                          padding: '4px 8px', 
                          background: 'rgba(102, 126, 234, 0.3)', 
                          border: '1px solid rgba(102, 126, 234, 0.5)', 
                          borderRadius: '4px', 
                          color: '#fff', 
                          textDecoration: 'none', 
                          fontSize: '10px' 
                        }}
                      >
                        📄 View PDF
                      </a>
                    )}
                    {attachment.warningMessage && (
                      <span style={{ 
                        fontSize: '10px', 
                        color: '#ffaa00', 
                        marginLeft: '8px',
                        fontStyle: 'italic'
                      }}>
                        {attachment.warningMessage}
                      </span>
                    )}
                  </div>
                  {attachment.isPdfFile && attachment.previewUrl && (
                    <div style={{ 
                      border: '1px solid rgba(255, 255, 255, 0.2)', 
                      borderRadius: '8px', 
                      overflow: 'hidden',
                      background: '#fff',
                      marginTop: '8px'
                    }}>
                      <iframe
                        src={attachment.previewUrl}
                        style={{
                          width: '100%',
                          height: '300px',
                          border: 'none'
                        }}
                        title={`PDF: ${attachment.name}`}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {image && (
        <div className="image-container">
          <div className="image-header">
            <Image size={16} />
            <span>Generated Image</span>
          </div>
          <img src={image} alt="AI Generated" className="generated-image" />
        </div>
      )}
      
      {parts.map((part, index) => {
        if (part.type === 'text') {
          return (
            <div key={index} className="text-content">
              {formatText(part.content)}
            </div>
          );
        } else if (part.type === 'code') {
          const isWeb = isWebContent(part.content, part.language);
          const showPreview = previewStates[part.id] !== undefined ? previewStates[part.id] : isWeb;
          
          return (
            <div key={index} className="code-block-container">
              <div className="code-header">
                <span className="language-tag">{part.language || 'text'}</span>
                <div className="code-actions">
                  {isWeb && (
                    <button
                      className="action-btn preview-btn"
                      onClick={() => togglePreview(part.id)}
                      title={showPreview ? 'Show Code' : 'Preview'}
                    >
                      {showPreview ? <Code size={16} /> : <Eye size={16} />}
                    </button>
                  )}
                  <button
                    className="action-btn copy-btn"
                    onClick={() => copyToClipboard(part.content, part.id)}
                    title="Copy code"
                  >
                    {copiedStates[part.id] ? <Check size={16} /> : <Copy size={16} />}
                  </button>
                </div>
              </div>
              
              {showPreview ? (
                <div className="preview-container">
                  {renderWebPreview(part.content)}
                </div>
              ) : (
                <pre className="code-content">
                  <code className={`language-${part.language}`}>
                    {part.content}
                  </code>
                </pre>
              )}
            </div>
          );
        }
        
        return null;
      })}
      
      {role === 'assistant' && !parts.some(p => p.type === 'code') && (
        <button
          className="message-copy-btn"
          onClick={() => copyToClipboard(content, 'message')}
          title="Copy message"
        >
          {copiedStates['message'] ? <Check size={14} /> : <Copy size={14} />}
        </button>
      )}
    </div>
  );
};

export default MessageRenderer;